import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { Props as FormattedMessageProps } from "react-intl/lib/src/components/message";
import { useOrgName } from "@src/common/reliablePartnerApi";
import LinkToCompanyAccount, {
  LinkToCompanyAccountProps,
} from "@src/i18n/components/LinkToCompanyAccount";
import LinkToCompanyContactDetails, {
  LinkToCompanyContactDetailsProps,
} from "@src/i18n/components/LinkToCompanyContactDetails";
import LinkToCompanyInvoicingDetails, {
  LinkToCompanyInvoicingDetailsProps,
} from "@src/i18n/components/LinkToCompanyInvoicingDetails";
import LinkToReportService, {
  LinkToReportServiceProps,
} from "@src/i18n/components/LinkToReportService";
import LinkToUpdaterProfile, {
  LinkToUpdaterProfileProps,
} from "@src/i18n/components/LinkToUpdaterProfile";
import MailToCustomerService, {
  MailToCustomerServiceProps,
} from "@src/i18n/components/MailToCustomerService";
import LinkToCustomerService from "@src/i18n/components/LinkToCustomerService";
import LinkToLk2Service from "@src/i18n/components/LinkToLk2Service";
import LinkToRPInstructionCenter from "@src/i18n/components/LinkToRPInstructionCenter";
import LinkToAccidentInsuranceForm from "@src/i18n/components/LinkToAccidentInsuranceForm";
import LinkToLiabilityInsuranceForm from "@src/i18n/components/LinkToLiabilityInsuranceForm";
import LinkToPricelist from "@src/i18n/components/LinkToPricelist";
import LinkToToS from "@src/i18n/components/LinkToToS";
import LinkToZeckit, {
  LinkToZeckitProps,
} from "@src/i18n/components/LinkToZeckit";
import LinkToLogoInstructions from "@src/i18n/components/LinkToLogoInstructions";
import { useAppSelector } from "@src/reduxStore";

interface RPFormattedMessageProps extends FormattedMessageProps {
  extraRTEProps?: {
    linkToCompanyAccount?: LinkToCompanyAccountProps;
    linkToCompanyContactDetails?: LinkToCompanyContactDetailsProps;
    linkToCompanyInvoicingDetails?: LinkToCompanyInvoicingDetailsProps;
    linkToReportService?: LinkToReportServiceProps;
    linkToUpdaterProfile?: LinkToUpdaterProfileProps;
    linkToZeckit?: LinkToZeckitProps;
    mailToCustomerService?: MailToCustomerServiceProps;
  };
}

const RPFormattedMessage: FC<RPFormattedMessageProps> = ({
  extraRTEProps,
  values,
  ...rest
}) => {
  const config = useAppSelector(state => state.common.config);
  const orgName = useOrgName();
  const defaultValues = {
    i: (chunks: HTMLElement) => <i>{chunks}</i>,
    b: (chunks: HTMLElement) => <b>{chunks}</b>,
    p: (chunks: HTMLElement) => <p>{chunks}</p>,
    small: (chunks: HTMLElement) => <small>{chunks}</small>,
    span: (chunks: HTMLElement) => <span>{chunks}</span>,
    orgName,
    bolUrl: config?.bolUrl,
    companiesUrl: config?.companiesUrl,
    portalUrl: config?.portalUrl,
    supportUrl: config?.supportUrl,
    vgUrl: config?.vgUrl,
    zeckitUrl: config?.zeckitUrl,
    linkToAccidentInsuranceForm: (chunks: HTMLElement) => (
      <LinkToAccidentInsuranceForm>{chunks}</LinkToAccidentInsuranceForm>
    ),
    linkToCompanyAccount: (chunks: HTMLElement) => (
      <LinkToCompanyAccount // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToCompanyAccount}
      >
        {chunks}
      </LinkToCompanyAccount>
    ),
    linkToCompanyContactDetails: (chunks: HTMLElement) => (
      <LinkToCompanyContactDetails // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToCompanyContactDetails}
      >
        {chunks}
      </LinkToCompanyContactDetails>
    ),
    linkToCompanyInvoicingDetails: (chunks: HTMLElement) => (
      <LinkToCompanyInvoicingDetails // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToCompanyInvoicingDetails}
      >
        {chunks}
      </LinkToCompanyInvoicingDetails>
    ),
    linkToCustomerService: (chunks: HTMLElement) => (
      <LinkToCustomerService>{chunks}</LinkToCustomerService>
    ),
    linkToLk2Service: (chunks: HTMLElement) => (
      <LinkToLk2Service>{chunks}</LinkToLk2Service>
    ),
    linkToLiabilityInsuranceForm: (chunks: HTMLElement) => (
      <LinkToLiabilityInsuranceForm>{chunks}</LinkToLiabilityInsuranceForm>
    ),
    linkToLogoInstructions: (chunks: HTMLElement) => (
      <LinkToLogoInstructions>{chunks}</LinkToLogoInstructions>
    ),
    linkToPricelist: (chunks: HTMLElement) => (
      <LinkToPricelist>{chunks}</LinkToPricelist>
    ),
    linkToReportService: (chunks: HTMLElement) => (
      <LinkToReportService // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToReportService}
      >
        {chunks}
      </LinkToReportService>
    ),
    linkToRPInstructionCenter: (chunks: HTMLElement) => (
      <LinkToRPInstructionCenter>{chunks}</LinkToRPInstructionCenter>
    ),
    linkToToS: (chunks: HTMLElement) => <LinkToToS>{chunks}</LinkToToS>,
    linkToUpdaterProfile: (chunks: HTMLElement) => (
      <LinkToUpdaterProfile // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToUpdaterProfile}
      >
        {chunks}
      </LinkToUpdaterProfile>
    ),
    linkToZeckit: (chunks: HTMLElement) => (
      <LinkToZeckit // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.linkToZeckit}
      >
        {chunks}
      </LinkToZeckit>
    ),
    mailToCustomerService: (chunks: HTMLElement) => (
      <MailToCustomerService // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
        {...extraRTEProps?.mailToCustomerService}
      >
        {chunks}
      </MailToCustomerService>
    ),
  };
  return (
    <FormattedMessage // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
      values={{ ...defaultValues, ...values }}
      {...rest}
    />
  );
};

export default RPFormattedMessage;
